import * as React from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import { Select, Button, Empty, Alert } from 'antd';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import { ZoomStatus } from 'types';
import {
  useZoomLessons,
  ZoomLessonRow,
  useZoomRecordings,
  useZoomMutations,
} from 'core/hooks';
import { todayInWarsaw, toApiDate, formatWarsaw } from 'core/utils/zoomTime';
import { lessonStatus } from 'core/utils/zoomLesson';
import { ZoomDatePicker, ZoomStatusBadge } from 'molecules';

const REFETCH_DELAY = 2_000;

/** A lesson counts as "needs a recording" when it has none, or its recording
 *  ended up failed/skipped. */
const needsRecording = (lesson: ZoomLessonRow): boolean => {
  const status = lessonStatus(lesson);
  return (
    status === ZoomStatus.NO_RECORDING ||
    status === ZoomStatus.FAILED ||
    status === ZoomStatus.SKIPPED
  );
};

export const Matching = memo(() => {
  const { t } = useTranslation();
  const location = useLocation();

  const initialDate = useMemo(() => {
    const parsed = queryString.parse(location.search);
    return typeof parsed.date === 'string'
      ? DateTime.fromISO(parsed.date)
      : todayInWarsaw();
  }, [location.search]);

  const [date, setDate] = useState<DateTime>(initialDate);
  const [groupId, setGroupId] = useState<string | undefined>();
  const [selectedLesson, setSelectedLesson] = useState<string>();
  const [selectedRecording, setSelectedRecording] = useState<string>();

  const apiDate = toApiDate(date);

  // All lessons of the day across every Zoom-enabled group. The group filter
  // narrows the left panel client-side, so the option list stays stable.
  const {
    lessons,
    loading: lessonsLoading,
    error: lessonsError,
    operations: lessonOps,
  } = useZoomLessons({ dateFrom: apiDate, dateTo: apiDate }, 200);

  const groupOptions = useMemo(() => {
    const seen = new Map<string, string>();
    lessons.forEach((lesson) => {
      const group = lesson.module?.group;
      if (group && !seen.has(group.id)) {
        seen.set(group.id, group.name);
      }
    });
    return Array.from(seen, ([value, label]) => ({ value, label }));
  }, [lessons]);

  const pendingLessons = useMemo(
    () =>
      lessons
        .filter(needsRecording)
        .filter((lesson) =>
          groupId ? lesson.module?.group?.id === groupId : true,
        ),
    [lessons, groupId],
  );

  // The selected lesson's Zoom account — orphan recordings are scoped to it so
  // a recording from another account can never be matched here by mistake.
  const selectedLessonObj = pendingLessons.find(
    (lesson) => lesson.id === selectedLesson,
  );
  const selectedAccount = selectedLessonObj?.module?.group?.zoomAccount ?? null;

  const {
    data: orphans,
    error: orphansError,
    operations: orphanOps,
  } = useZoomRecordings({
    orphanOnly: true,
    dateFrom: apiDate,
    dateTo: apiDate,
    accountId: selectedAccount?.id,
  });
  const mutations = useZoomMutations();

  const screenError = lessonsError ?? orphansError;

  const reloadBoth = useCallback(() => {
    window.setTimeout(() => {
      lessonOps.refetch();
      orphanOps.refetch();
    }, REFETCH_DELAY);
  }, [lessonOps, orphanOps]);

  const onBind = useCallback(async () => {
    if (!selectedLesson || !selectedRecording) {
      return;
    }
    await mutations.manualMatch.run(selectedLesson, selectedRecording);
    setSelectedLesson(undefined);
    setSelectedRecording(undefined);
    reloadBoth();
  }, [selectedLesson, selectedRecording, mutations, reloadBoth]);

  const onMarkMissing = useCallback(
    async (lessonId: string) => {
      await mutations.markMissing.run(lessonId);
      reloadBoth();
    },
    [mutations, reloadBoth],
  );

  const onRetry = useCallback(
    async (recordingId: string) => {
      await mutations.retry.run(recordingId);
      reloadBoth();
    },
    [mutations, reloadBoth],
  );

  return (
    <Wrapper>
      {screenError ? (
        <Alert
          type="error"
          showIcon
          message={screenError}
          style={{ marginBottom: 12 }}
        />
      ) : null}
      <Toolbar>
        <ZoomDatePicker value={date} onChange={setDate} />
        <Select
          showSearch
          allowClear
          optionFilterProp="label"
          style={{ width: 280 }}
          placeholder={t('pages.zoom.matching.selectGroup')}
          value={groupId}
          options={groupOptions}
          onChange={(value) => {
            setGroupId(value as string | undefined);
            setSelectedLesson(undefined);
            setSelectedRecording(undefined);
          }}
        />
        <BindButton
          type="primary"
          disabled={!selectedLesson || !selectedRecording}
          loading={mutations.manualMatch.loading}
          onClick={onBind}
        >
          {t('pages.zoom.matching.bind')}
        </BindButton>
      </Toolbar>

      <Panels>
        <Panel>
          <PanelTitle>
            {t('pages.zoom.matching.lessonsTitle')} ({pendingLessons.length})
          </PanelTitle>
          {!lessonsLoading && !pendingLessons.length ? (
            <Empty description={t('pages.zoom.matching.allMatched')} />
          ) : null}
          {pendingLessons.map((lesson) => {
            const failed = lessonStatus(lesson) === ZoomStatus.FAILED;
            const group = lesson.module?.group;
            return (
              <Card
                key={lesson.id}
                selected={selectedLesson === lesson.id}
                onClick={() => {
                  setSelectedLesson(lesson.id);
                  setSelectedRecording(undefined);
                }}
              >
                <CardTitle>{lesson.name}</CardTitle>
                <CardMeta>
                  {group ? `${group.name} · ` : ''}
                  {lesson.date} · {lesson.startTime?.slice(0, 5)}–
                  {lesson.endTime?.slice(0, 5)}
                </CardMeta>
                <CardActions>
                  <Button
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      onMarkMissing(lesson.id);
                    }}
                  >
                    {t('pages.zoom.matching.markMissing')}
                  </Button>
                  {failed && lesson.zoomRecordingMeta ? (
                    <Button
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        onRetry(lesson.zoomRecordingMeta!.id);
                      }}
                    >
                      {t('pages.zoom.matching.retry')}
                    </Button>
                  ) : null}
                </CardActions>
              </Card>
            );
          })}
        </Panel>

        <Panel>
          <PanelTitle>
            {t('pages.zoom.matching.orphansTitle')}
            {selectedAccount ? ` · ${selectedAccount.displayName}` : ''} (
            {orphans.length})
          </PanelTitle>
          {!selectedLesson ? (
            <Empty description={t('pages.zoom.matching.pickLesson')} />
          ) : null}
          {selectedLesson && !orphans.length ? (
            <Empty description={t('pages.zoom.matching.noOrphans')} />
          ) : null}
          {selectedLesson
            ? orphans.map((recording) => (
                <Card
                  key={recording.id}
                  selected={selectedRecording === recording.id}
                  disabled={recording.deletedFromZoom}
                  onClick={() => {
                    if (!recording.deletedFromZoom) {
                      setSelectedRecording(recording.id);
                    }
                  }}
                >
                  <CardTitle>{recording.topic}</CardTitle>
                  <CardMeta>
                    {recording.zoomAccount?.displayName} ·{' '}
                    {formatWarsaw(recording.startTime)} ·{' '}
                    {recording.durationMinutes} {t('pages.zoom.recordings.min')}
                  </CardMeta>
                  {recording.deletedFromZoom ? (
                    <ZoomStatusBadge status={ZoomStatus.SKIPPED} />
                  ) : null}
                </Card>
              ))
            : null}
        </Panel>
      </Panels>
    </Wrapper>
  );
});

const Wrapper = styled.div`
  width: 100%;
  padding: 16px 0;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const BindButton = styled(Button)`
  margin-left: auto;
`;

const Panels = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
`;

const Panel = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  min-height: 320px;
`;

const PanelTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 16px;
`;

const Card = styled.div<{ selected?: boolean; disabled?: boolean }>`
  border: 1px solid ${({ selected }) => (selected ? '#2563eb' : '#e5e7eb')};
  background: ${({ selected }) => (selected ? '#eff6ff' : '#ffffff')};
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const CardTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const CardMeta = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-top: 4px;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
`;
